import React, { useState } from "react";
import { X } from "lucide-react";
import { motion } from "framer-motion";
import { User } from "../../types/types";
import { usePostAuth } from "../../hooks/usePostAuth";
import {
  modalBackdropVariants,
  modalContentVariants,
} from "../../animations/animation";

interface UpdateUserPhotoModalProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
  invalidateKey?: string[];
}

const UpdateUserPhotoModal: React.FC<UpdateUserPhotoModalProps> = ({
  user,
  onClose,
  onSuccess,
  invalidateKey = ["get-alumnis"],
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    user.photo_url || null
  );

  const { mutate: updateUserPhoto, isPending: loading } = usePostAuth(
    `/users/${user.id}`,
    { queryKeyToInvalidate: invalidateKey }
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      alert("Please select a photo to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("photo", selectedFile);
    formData.append("_method", "patch");

    updateUserPhoto(formData, {
      onSuccess: () => {
        onSuccess();
        onClose();
      },
      onError: () => {
        alert("Failed to update photo");
      },
    });
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      variants={modalBackdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
        variants={modalContentVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6 relative">
          <h3 className="text-xl font-bold text-gray-900">
            Update Profile Photo
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col items-center gap-4 mb-6">
            <div className="relative">
              <img
                src={
                  previewUrl ||
                  user.photo_url ||
                  "https://via.placeholder.com/150"
                }
                alt="Profile preview"
                className="w-32 h-32 rounded-full object-cover border-2 border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload New Photo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedFile}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white rounded-md font-medium transition"
            >
              {loading ? "Uploading..." : "Update Photo"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default UpdateUserPhotoModal;
